import { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
    Activity, 
    Lock, 
    Loader2, 
    AlertCircle,
    CheckCircle2,
    Eye,
    EyeOff,
    Shield
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const navigate = useNavigate();
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const validatePassword = () => {
        if (password.length < 8) return t('resetPassword.minChars');
        if (!/[A-Z]/.test(password)) return t('resetPassword.uppercase');
        if (!/[a-z]/.test(password)) return t('resetPassword.lowercase');
        if (!/[0-9]/.test(password)) return t('resetPassword.number');
        if (password !== confirmPassword) return t('errors.passwordMismatch');
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const validationError = validatePassword();
        if (validationError) {
            setError(validationError);
            return;
        }
        
        setError('');
        setLoading(true);

        try {
            await axios.post(`${API}/auth/reset-password`, {
                token,
                new_password: password
            });
            setSuccess(true);
            toast.success(t('resetPassword.success'));
        } catch (err) {
            const message = err.response?.data?.detail || t('errors.generic');
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-sky-50/30">
                <Card className="border-0 shadow-2xl shadow-slate-200/50 max-w-md w-full">
                    <CardContent className="p-8 text-center">
                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('resetPassword.invalidLink')}</h3>
                        <p className="text-slate-500 mb-6">
                            {t('resetPassword.invalidLinkDesc')}
                        </p>
                        <Link to="/forgot-password">
                            <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl">
                                {t('resetPassword.requestNewLink')}
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-sky-50/30" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#0D2137] flex items-center justify-center shadow-xl">
                            <Activity className="w-7 h-7 text-teal-400" />
                        </div>
                        <div>
                            <span className="text-xl font-bold text-slate-800">{t('app.name')}</span>
                            <span className="block text-xs text-slate-500">{t('app.subtitle')}</span>
                        </div>
                    </Link>
                </div>

                <Card className="border-0 shadow-2xl shadow-slate-200/50">
                    <CardContent className="p-8">
                        {!success ? (
                            <>
                                <div className="text-center mb-8">
                                    <div className="w-16 h-16 rounded-2xl bg-teal-50 flex items-center justify-center mx-auto mb-4">
                                        <Lock className="w-8 h-8 text-teal-600" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-slate-800 mb-2">{t('resetPassword.title')}</h2>
                                    <p className="text-slate-500">
                                        {t('resetPassword.subtitle')}
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {error && (
                                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                            {error}
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label htmlFor="password" className="text-slate-700">{t('resetPassword.newPassword')}</Label>
                                        <div className="relative">
                                            <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="••••••••"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="ps-10 pe-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                                                data-testid="reset-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="confirmPassword" className="text-slate-700">{t('resetPassword.confirmNewPassword')}</Label>
                                        <div className="relative">
                                            <Lock className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <Input
                                                id="confirmPassword"
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                placeholder="••••••••"
                                                value={confirmPassword}
                                                onChange={(e) => setConfirmPassword(e.target.value)}
                                                required
                                                className="ps-10 pe-10 h-12 rounded-xl border-slate-200 focus:border-teal-500 focus:ring-teal-500/20"
                                                data-testid="reset-confirm-password-input"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className="absolute end-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                            >
                                                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Password requirements */}
                                    <div className="p-3 bg-slate-50 rounded-xl text-sm space-y-1">
                                        <p className="font-medium text-slate-700 mb-2">{t('resetPassword.requirements')}</p>
                                        <p className={password.length >= 8 ? 'text-emerald-600' : 'text-slate-500'}>
                                            {password.length >= 8 ? '✓' : '○'} {t('resetPassword.minChars')}
                                        </p>
                                        <p className={/[A-Z]/.test(password) ? 'text-emerald-600' : 'text-slate-500'}>
                                            {/[A-Z]/.test(password) ? '✓' : '○'} {t('resetPassword.uppercase')}
                                        </p>
                                        <p className={/[a-z]/.test(password) ? 'text-emerald-600' : 'text-slate-500'}>
                                            {/[a-z]/.test(password) ? '✓' : '○'} {t('resetPassword.lowercase')}
                                        </p>
                                        <p className={/[0-9]/.test(password) ? 'text-emerald-600' : 'text-slate-500'}>
                                            {/[0-9]/.test(password) ? '✓' : '○'} {t('resetPassword.number')}
                                        </p>
                                    </div>

                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium shadow-lg shadow-teal-500/25"
                                        data-testid="reset-submit-btn"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="me-2 w-5 h-5 animate-spin" />
                                                {t('resetPassword.resetting')}
                                            </>
                                        ) : (
                                            t('resetPassword.resetButton')
                                        )}
                                    </Button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-6">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                                    <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-semibold text-slate-800 mb-2">{t('resetPassword.success')}</h3>
                                <p className="text-slate-500 mb-6">
                                    {t('resetPassword.successDesc')}
                                </p>
                                <div className="p-4 bg-teal-50 rounded-xl border border-teal-200 text-sm text-teal-700 mb-6">
                                    <Shield className="w-4 h-4 inline me-2" />
                                    {t('resetPassword.securityNote')}
                                </div>
                                <Link to="/login">
                                    <Button className="bg-teal-500 hover:bg-teal-600 text-white rounded-xl">
                                        {t('resetPassword.signInNow')}
                                    </Button>
                                </Link>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
